import type {
  BookableEventFormData,
  EventFormData,
  NonBookableEventFormData,
} from "@/lib/event-schemas";
import type { AdminEventDetail } from "@/lib/admin-api";

function resolveEventKind(loadedEvent: AdminEventDetail): "STANDARD" | "RACE" {
  const eventKind = (loadedEvent as { eventKind?: string }).eventKind ?? "STANDARD";
  return eventKind === "RACE" ? "RACE" : "STANDARD";
}

function buildBookableEditFormValues(
  loadedEvent: AdminEventDetail,
): BookableEventFormData {
  return {
    isBookable: true,
    eventKind: resolveEventKind(loadedEvent),
    isRecurring: false,
    rrule: "",
    imagePool: [],
    timezone: loadedEvent.timezone,
    title: loadedEvent.title,
    description: loadedEvent.description,
    schedule: loadedEvent.schedule ?? null,
    whatToBring: loadedEvent.whatToBring ?? null,
    location: loadedEvent.location ?? null,
    audience: loadedEvent.audience ?? "OPEN",
    requiresConsentForm: loadedEvent.requiresConsentForm ?? false,
    requiresPhotoConsent: loadedEvent.requiresPhotoConsent ?? false,
    groupsEnabled: loadedEvent.groupsEnabled ?? false,
    isPublished: loadedEvent.isPublished ?? false,
    isFeatured: loadedEvent.isFeatured ?? false,
    isCancelled: loadedEvent.isCancelled ?? false,
    imageUrl: loadedEvent.imageUrl ?? null,
    bookingClosesAt: loadedEvent.bookingClosesAt
      ? new Date(loadedEvent.bookingClosesAt)
      : null,
    tags: loadedEvent.tags.map((t) => t.tag.name),
    sessions: loadedEvent.sessions
      .filter(
        (s): s is typeof s & { startsAt: string; endsAt: string } =>
          typeof s.startsAt === "string" && typeof s.endsAt === "string",
      )
      .map((s) => ({
        startsAt: new Date(s.startsAt),
        endsAt: new Date(s.endsAt),
        label: s.label || undefined,
      })),
    pricePoints: loadedEvent.pricePoints.map((pp) => ({
      id: pp.id,
      label: pp.label,
      description: pp.description || null,
      priceCents: pp.priceCents,
      currency: "gbp",
      sortOrder: pp.sortOrder,
      availableFrom: pp.availableFrom ? new Date(pp.availableFrom) : null,
      availableUntil: pp.availableUntil ? new Date(pp.availableUntil) : null,
      depositCents: pp.depositCents ?? null,
      balanceDueAt: pp.balanceDueAt ? new Date(pp.balanceDueAt) : null,
      capacity: pp.capacity ?? null,
    })),
  };
}

function buildNonBookableEditFormValues(
  loadedEvent: AdminEventDetail,
): NonBookableEventFormData {
  return {
    isBookable: false,
    eventKind: resolveEventKind(loadedEvent),
    isRecurring: false,
    rrule: "",
    timezone: loadedEvent.timezone,
    title: loadedEvent.title,
    description: loadedEvent.description,
    location: loadedEvent.location ?? null,
    isPublished: loadedEvent.isPublished ?? false,
    isFeatured: false,
    isCancelled: loadedEvent.isCancelled ?? false,
    groupsEnabled: false,
    tags: loadedEvent.tags.map((t) => t.tag.name),
    startsAt: loadedEvent.startsAt ? new Date(loadedEvent.startsAt) : new Date(),
    endsAt: loadedEvent.endsAt ? new Date(loadedEvent.endsAt) : new Date(),
  };
}

export function buildEditFormValues(loadedEvent: AdminEventDetail): EventFormData {
  if ((loadedEvent.isBookable ?? true) === false) {
    return buildNonBookableEditFormValues(loadedEvent);
  }
  return buildBookableEditFormValues(loadedEvent);
}

/**
 * Builds create-form values for a new event cloned from an existing one.
 * The clone is always a draft and price points are detached from their
 * original rows so the API creates fresh records.
 */
export function buildClonedEventFormValues(
  sourceEvent: AdminEventDetail,
): EventFormData {
  const values = buildEditFormValues(sourceEvent);
  if (!values.isBookable) {
    return { ...values, isPublished: false, isCancelled: false };
  }
  return {
    ...values,
    isPublished: false,
    isCancelled: false,
    pricePoints: values.pricePoints.map(({ id: _id, ...pp }) => pp),
  };
}

/**
 * The date all other event dates are shifted relative to: the earliest session
 * start for bookable events (matching the API's envelope/RRULE anchor), or the
 * explicit start for non-bookable events.
 */
export function getEventFormAnchorDate(values: EventFormData): Date | null {
  if (!values.isBookable) return values.startsAt;
  if (values.sessions.length === 0) return null;
  return values.sessions.reduce((earliest, s) =>
    s.startsAt.getTime() < earliest.getTime() ? s.startsAt : earliest,
  values.sessions[0].startsAt);
}

function shiftDate(date: Date, deltaMs: number): Date {
  return new Date(date.getTime() + deltaMs);
}

function shiftNullableDate(
  date: Date | null | undefined,
  deltaMs: number,
): Date | null {
  return date ? shiftDate(date, deltaMs) : null;
}

/**
 * Shifts every date/time field in the form values by `deltaMs`, preserving
 * the relative offsets between them (mirrors the API's recurring-series logic).
 */
export function shiftEventFormDates(
  values: EventFormData,
  deltaMs: number,
): EventFormData {
  if (!values.isBookable) {
    return {
      ...values,
      startsAt: shiftDate(values.startsAt, deltaMs),
      endsAt: shiftDate(values.endsAt, deltaMs),
    };
  }
  return {
    ...values,
    bookingClosesAt: shiftNullableDate(values.bookingClosesAt, deltaMs),
    sessions: values.sessions.map((s) => ({
      ...s,
      startsAt: shiftDate(s.startsAt, deltaMs),
      endsAt: shiftDate(s.endsAt, deltaMs),
    })),
    pricePoints: values.pricePoints.map((pp) => ({
      ...pp,
      availableFrom: shiftNullableDate(pp.availableFrom, deltaMs),
      availableUntil: shiftNullableDate(pp.availableUntil, deltaMs),
      balanceDueAt: shiftNullableDate(pp.balanceDueAt, deltaMs),
    })),
  };
}
